import React, { useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../translations';
import {
  PanelContainer, Title, Subtitle, PrimaryButton, SecondaryButton,
  PageHeader, TitleRow, IconButton,
} from '../ui/common';
import { ArrowLeftIcon } from '../ui/icons';
import { API_BASE } from '../config';
import { buildErrorReportCsv, buildWarningRows, splitRowsByAction } from '../utils/importReport';
import {
  AlertIcon,
  ArrowRightIcon,
  CheckIcon,
  DownloadIcon,
  PackageIcon,
  SheetIcon,
  UploadIcon,
  UsersIcon,
  XIcon,
} from '../ui/icons';
import { TylkoOdczytWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

const ENTITIES = ['equipment', 'clients'];

function csrfHeader() {
  return { 'X-CSRF-Token': localStorage.getItem('csrfToken') || '' };
}

function filenameFromContentDisposition(header, fallback) {
  const match = /filename="([^"]+)"/.exec(header || '');
  return match ? match[1] : fallback;
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ImportData() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const entityFromQuery = searchParams.get('entity');

  const [entity, setEntity] = useState(ENTITIES.includes(entityFromQuery) ? entityFromQuery : null);
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null); // { checksum, summary, rows, note }
  const [commitResult, setCommitResult] = useState(null); // { inserted, updated, skipped, errors }
  const fileInputRef = useRef(null);

  const entityLabel = (e) => (e === 'equipment' ? t('import.entity.equipment') : t('import.entity.clients'));

  const resetWizard = () => {
    setStep(1);
    setFile(null);
    setError('');
    setPreview(null);
    setCommitResult(null);
    setDownloaded(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const chooseEntity = (e) => {
    setEntity(e);
    resetWizard();
  };

  const handleDownloadTemplate = useCallback(async () => {
    setError('');
    setDownloading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/import/template?entity=${entity}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const filename = filenameFromContentDisposition(
        res.headers['content-disposition'],
        `szablon-import-${entity}.xlsx`
      );
      downloadBlob(res.data, filename);
      // Pobranie pliku nie zostawia zadnego sladu w oknie przegladarki, wiec bez tej flagi
      // uzytkownik nie wie, czy przycisk w ogole zadzialal.
      setDownloaded(true);
    } catch (err) {
      setError(t('import.downloadError'));
    } finally {
      setDownloading(false);
    }
  }, [entity, t]);

  // Wspolne dla `<input>` i upuszczenia pliku: przy upuszczeniu atrybut `accept` nie obowiazuje,
  // wiec rozszerzenie trzeba sprawdzic samemu — inaczej .csv poszedlby na serwer po blad 400.
  const acceptFile = (picked) => {
    if (!picked) return;
    if (!/\.xlsx$/i.test(picked.name)) {
      setFile(null);
      setError(t('import.fileTypeError'));
      return;
    }
    setFile(picked);
    setError('');
  };

  const handleFileChange = (e) => {
    acceptFile(e.target.files && e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    acceptFile(e.dataTransfer?.files?.[0]);
  };

  const clearFile = () => {
    setFile(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const readErrorMessage = (err, fallbackKey) => {
    // 503: serwer chwilowo nie ma miejsca na wgrywane pliki (budżet ciał w pamięci, routes/import.js).
    // Plik jest w porządku, więc zamiast komunikatu serwera mówimy, żeby ponowić; plik i podgląd zostają.
    if (err?.response?.status === 503) return t('import.serverBusy');
    const data = err?.response?.data;
    return (data && (data.message || data.error)) || t(fallbackKey);
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setError('');
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_BASE}/api/import/${entity}/preview`, formData, {
        withCredentials: true,
        headers: csrfHeader(),
      });
      setPreview(res.data);
      setCommitResult(null);
      setStep(3);
    } catch (err) {
      setError(readErrorMessage(err, 'import.errorGeneric'));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDownloadErrorReport = () => {
    if (!preview) return;
    const csv = buildErrorReportCsv(preview.rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    downloadBlob(blob, `import-${entity}-bledy.csv`);
  };

  const handleCommit = async () => {
    if (!file || !preview) return;
    setError('');
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('checksum', preview.checksum);
      const res = await axios.post(`${API_BASE}/api/import/${entity}/commit`, formData, {
        withCredentials: true,
        headers: csrfHeader(),
      });
      setCommitResult(res.data);
    } catch (err) {
      if (err?.response?.status === 409 && err?.response?.data?.error === 'checksum_mismatch') {
        setError(t('import.checksumMismatch'));
        setPreview(null);
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setStep(2);
      } else {
        setError(readErrorMessage(err, 'import.errorGeneric'));
      }
    } finally {
      setImporting(false);
    }
  };

  if (!entity) {
    return (
      <PanelContainer>
        <Page>
          {/* Przycisk powrotu ujednolicony z resztą podstron profilu (IconButton + strzałka,
              tuż przed tytułem) — wcześniej był osobnym przyciskiem tekstowym po prawej. */}
          <PageHeader>
            <TitleRow>
              <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
                <ArrowLeftIcon />
              </IconButton>
              <div>
                <Title>{t('import.title')}</Title>
                <Subtitle>{t('import.subtitle')}</Subtitle>
              </div>
            </TitleRow>
          </PageHeader>
          <EntityGrid>
            {ENTITIES.map((e) => (
              <EntityCard key={e} type="button" data-cy={`import-entity-${e}`} onClick={() => chooseEntity(e)}>
                <EntityIcon aria-hidden="true">
                  {e === 'equipment' ? <PackageIcon size={22} /> : <UsersIcon size={22} />}
                </EntityIcon>
                <EntityText>
                  <EntityName>{entityLabel(e)}</EntityName>
                  <EntityDesc>{t(`import.entity.${e}.description`)}</EntityDesc>
                </EntityText>
                <EntityArrow aria-hidden="true"><ArrowRightIcon size={18} /></EntityArrow>
              </EntityCard>
            ))}
          </EntityGrid>
        </Page>
      </PanelContainer>
    );
  }

  const { toInsert, toUpdate, invalid } = preview ? splitRowsByAction(preview.rows) : { toInsert: [], toUpdate: [], invalid: [] };
  const warningRows = preview ? buildWarningRows(preview.rows) : [];
  const canCommit = preview && (toInsert.length + toUpdate.length) > 0;
  // Po zapisie wszystkie trzy kroki sa za nami — stad 4, a nie `step`, ktory zostaje na 3.
  const railStep = commitResult ? 4 : step;
  const stepLabels = [t('import.step.template'), t('import.step.file'), t('import.step.review')];

  return (
    <PanelContainer>
      <Page>
        <PageHeader>
          {/* Strzalka cofania ujednolicona z reszta podstron profilu (IconButton + strzalka,
              tuz przed tytulem) — wczesniej byl tu osobny przycisk tekstowy „Zmien rodzaj danych". */}
          <TitleRow>
            <IconButton onClick={() => { setEntity(null); navigate('/import'); }} aria-label={t('import.changeEntity')}>
              <ArrowLeftIcon />
            </IconButton>
            <div>
              <Title>{t('import.title')}</Title>
              <Subtitle>
                <EntityTag>
                  {entity === 'equipment' ? <PackageIcon size={15} /> : <UsersIcon size={15} />}
                  {entityLabel(entity)}
                </EntityTag>
              </Subtitle>
            </div>
          </TitleRow>
        </PageHeader>
        <TylkoOdczytWPrzerwie>

        <Stepper aria-label={t('import.stepperLabel')}>
          {stepLabels.map((label, i) => {
            const n = i + 1;
            const done = railStep > n;
            const active = railStep === n;
            return (
              <StepItem key={n} $done={done}>
                <StepDot
                  type="button"
                  $active={active}
                  $done={done}
                  disabled={!done || !!commitResult}
                  aria-current={active ? 'step' : undefined}
                  onClick={() => setStep(n)}
                >
                  {done ? <CheckIcon size={16} /> : n}
                </StepDot>
                <StepLabel $active={active} $done={done}>{label}</StepLabel>
              </StepItem>
            );
          })}
        </Stepper>

        {error && (
          <ErrorBanner role="alert" data-cy="import-error">
            <AlertIcon size={18} />
            <span>{error}</span>
          </ErrorBanner>
        )}

        {step === 1 && (
          <StepCard>
            <StepTitle>{t('import.step1.title')}</StepTitle>
            <StepText>{t('import.step1.description')}</StepText>

            {entity === 'equipment' && (
              <ConfigBanner data-cy="import-config-note">
                <span>{t('import.step1.configNote')}</span>
                <a href="/inventory/add-category" onClick={(e) => { e.preventDefault(); navigate('/inventory/add-category'); }}>
                  {t('import.step1.configLink')}
                </a>
              </ConfigBanner>
            )}

            <TemplatePanel>
              <TemplateIcon aria-hidden="true"><SheetIcon size={26} /></TemplateIcon>
              <TemplateBody>
                <TemplateList>
                  <li><CheckIcon size={16} /><span>{t('import.step1.bullet.columns')}</span></li>
                  <li><CheckIcon size={16} /><span>{t(`import.step1.bullet.values.${entity}`)}</span></li>
                  <li><CheckIcon size={16} /><span>{t('import.step1.bullet.reuse')}</span></li>
                </TemplateList>
                <PrimaryButton data-cy="import-download-template" onClick={handleDownloadTemplate} disabled={downloading}>
                  <DownloadIcon size={18} />
                  {downloading ? t('import.downloading') : t('import.downloadTemplate')}
                </PrimaryButton>
                {downloaded && !downloading && (
                  <DownloadedNote role="status">
                    <CheckIcon size={16} />
                    {t('import.step1.downloaded')}
                  </DownloadedNote>
                )}
              </TemplateBody>
            </TemplatePanel>

            <StepActions>
              <SecondaryButton data-cy="import-goto-upload" onClick={() => setStep(2)}>
                {t('import.next')}
              </SecondaryButton>
            </StepActions>
          </StepCard>
        )}

        {step === 2 && (
          <StepCard>
            <StepTitle>{t('import.step2.title')}</StepTitle>
            <StepText>{t('import.step2.description')}</StepText>

            <Dropzone
              $active={dragActive}
              $filled={!!file}
              role="button"
              tabIndex={0}
              aria-label={t('import.dropzone.title')}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
            >
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                tabIndex={-1}
                data-cy="import-file-input"
                onChange={handleFileChange}
              />
              {file ? (
                <>
                  <FileCard data-cy="import-file-name">
                    <FileIcon aria-hidden="true"><SheetIcon size={24} /></FileIcon>
                    <FileMeta>
                      <FileNameText>{file.name}</FileNameText>
                      <FileSize>{formatBytes(file.size)}</FileSize>
                    </FileMeta>
                    <RemoveFile
                      type="button"
                      aria-label={t('import.file.remove')}
                      title={t('import.file.remove')}
                      onClick={(e) => { e.stopPropagation(); clearFile(); }}
                    >
                      <XIcon size={16} aria-hidden="true" />
                    </RemoveFile>
                  </FileCard>
                  <DropHint>{t('import.file.replace')}</DropHint>
                </>
              ) : (
                <>
                  <DropIcon aria-hidden="true"><UploadIcon size={28} /></DropIcon>
                  <DropTitle>{t('import.dropzone.title')}</DropTitle>
                  <DropHint>{t('import.dropzone.hint')}</DropHint>
                </>
              )}
            </Dropzone>

            <Constraints>
              <li>{t('import.constraint.format')}</li>
              <li>{t('import.constraint.size')}</li>
              <li>{t('import.constraint.rows')}</li>
            </Constraints>

            <StepActions>
              <SecondaryButton data-cy="import-back-to-template" onClick={() => setStep(1)}>{t('import.back')}</SecondaryButton>
              <PrimaryButton
                data-cy="import-analyze"
                onClick={handleAnalyze}
                disabled={!file || analyzing}
              >
                {analyzing ? t('import.analyzing') : t('import.analyze')}
              </PrimaryButton>
            </StepActions>
          </StepCard>
        )}

        {step === 3 && preview && !commitResult && (
          <StepCard>
            <StepTitle>{t('import.step3.title')}</StepTitle>
            <StepText>{t('import.step3.description')}</StepText>

            {preview.note && (
              <NoteBanner data-cy="import-note">
                <AlertIcon size={18} />
                <span>{preview.note}</span>
              </NoteBanner>
            )}

            <SummaryGrid>
              <SummaryCard data-cy="import-summary-total">
                <SummaryValue>{preview.summary.total}</SummaryValue>
                <SummaryLabel>{t('import.summary.total')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $tone="insert" $muted={!preview.summary.toInsert} data-cy="import-summary-insert">
                <SummaryValue $tone="insert" $muted={!preview.summary.toInsert}>{preview.summary.toInsert}</SummaryValue>
                <SummaryLabel>{t('import.summary.toInsert')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $tone="update" $muted={!preview.summary.toUpdate} data-cy="import-summary-update">
                <SummaryValue $tone="update" $muted={!preview.summary.toUpdate}>{preview.summary.toUpdate}</SummaryValue>
                <SummaryLabel>{t('import.summary.toUpdate')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $tone="invalid" $muted={!preview.summary.invalid} data-cy="import-summary-invalid">
                <SummaryValue $tone="invalid" $muted={!preview.summary.invalid}>{preview.summary.invalid}</SummaryValue>
                <SummaryLabel>{t('import.summary.invalid')}</SummaryLabel>
              </SummaryCard>
            </SummaryGrid>

            {invalid.length > 0 && (
              <RowsSection data-cy="import-invalid-rows">
                <RowsSectionHead>
                  <RowsSectionTitle $tone="invalid">
                    <AlertIcon size={17} />
                    {t('import.errors.title')}
                    <Count $tone="invalid">{invalid.length}</Count>
                  </RowsSectionTitle>
                  <SecondaryButton data-cy="import-download-error-report" onClick={handleDownloadErrorReport}>
                    <DownloadIcon size={16} />
                    {t('import.downloadErrorReport')}
                  </SecondaryButton>
                </RowsSectionHead>
                <RowsHint>{t('import.errors.hint')}</RowsHint>
                <RowsList>
                  {invalid.map((row) => (
                    <RowItem key={row.rowNumber} $tone="invalid">
                      <RowNumber $tone="invalid">{t('import.rowNumber')} {row.rowNumber}</RowNumber>
                      <RowMessages>
                        {row.errors.map((err, i) => (
                          <RowMessage key={i}>
                            <FieldPill>{err.field}</FieldPill>
                            <span>{err.message}</span>
                          </RowMessage>
                        ))}
                      </RowMessages>
                    </RowItem>
                  ))}
                </RowsList>
              </RowsSection>
            )}

            {warningRows.length > 0 && (
              <RowsSection data-cy="import-warning-rows">
                <RowsSectionHead>
                  <RowsSectionTitle $tone="warning">
                    <AlertIcon size={17} />
                    {t('import.warnings.title')}
                    <Count $tone="warning">{warningRows.length}</Count>
                  </RowsSectionTitle>
                </RowsSectionHead>
                <RowsHint>{t('import.warnings.hint')}</RowsHint>
                <RowsList>
                  {warningRows.map((row) => (
                    <RowItem key={row.rowNumber} $tone="warning">
                      <RowNumber $tone="warning">{t('import.rowNumber')} {row.rowNumber}</RowNumber>
                      <RowMessages>
                        {row.warnings.map((w, i) => (
                          <RowMessage key={i}>
                            <FieldPill>{w.field}</FieldPill>
                            <span>{w.message}</span>
                          </RowMessage>
                        ))}
                      </RowMessages>
                    </RowItem>
                  ))}
                </RowsList>
              </RowsSection>
            )}

            <StepActions>
              <SecondaryButton data-cy="import-back-to-upload" onClick={() => setStep(2)}>{t('import.back')}</SecondaryButton>
              <PrimaryButton
                data-cy="import-commit"
                onClick={handleCommit}
                disabled={!canCommit || importing}
              >
                {importing ? t('import.importing') : t('import.confirmImport')}
              </PrimaryButton>
            </StepActions>
          </StepCard>
        )}

        {commitResult && (
          <StepCard data-cy="import-result">
            <ResultHead>
              <ResultMark aria-hidden="true"><CheckIcon size={26} /></ResultMark>
              <div>
                <StepTitle>{t('import.result.title')}</StepTitle>
                <StepText>{t(`import.result.description.${entity}`)}</StepText>
              </div>
            </ResultHead>

            <SummaryGrid>
              <SummaryCard $tone="insert" $muted={!commitResult.inserted} data-cy="import-result-inserted">
                <SummaryValue $tone="insert" $muted={!commitResult.inserted}>{commitResult.inserted}</SummaryValue>
                <SummaryLabel>{t('import.result.inserted')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $tone="update" $muted={!commitResult.updated} data-cy="import-result-updated">
                <SummaryValue $tone="update" $muted={!commitResult.updated}>{commitResult.updated}</SummaryValue>
                <SummaryLabel>{t('import.result.updated')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $tone="invalid" $muted={!commitResult.skipped} data-cy="import-result-skipped">
                <SummaryValue $tone="invalid" $muted={!commitResult.skipped}>{commitResult.skipped}</SummaryValue>
                <SummaryLabel>{t('import.result.skipped')}</SummaryLabel>
              </SummaryCard>
            </SummaryGrid>

            <StepActions>
              <PrimaryButton
                data-cy="import-view-data"
                onClick={() => navigate(entity === 'equipment' ? '/inventory' : '/clients')}
              >
                {t(`import.result.view.${entity}`)}
              </PrimaryButton>
              <SecondaryButton data-cy="import-another" onClick={resetWizard}>{t('import.result.another')}</SecondaryButton>
            </StepActions>
          </StepCard>
        )}
        </TylkoOdczytWPrzerwie>
      </Page>
    </PanelContainer>
  );
}

/* ── Kolory statusow ────────────────────────────────────────────────────────────
   Trzymane jako zmienne na jednym pojemniku strony, bo poprzednia wersja miala je wpisane
   na stalo w kilku miejscach (#dc2626, #92400e) i w trybie ciemnym brazowy tekst ladowal na
   prawie czarnym tle. Tu kazdy odcien ma wariant dla obu motywow, ustawiony raz. */
const Page = styled.div`
  --imp-add: #15803d;
  --imp-add-bg: rgba(22, 163, 74, 0.08);
  --imp-add-line: rgba(22, 163, 74, 0.35);
  --imp-upd: var(--primary);
  --imp-upd-bg: rgba(59, 130, 246, 0.08);
  --imp-upd-line: rgba(59, 130, 246, 0.3);
  --imp-bad: #dc2626;
  --imp-bad-bg: rgba(220, 38, 38, 0.07);
  --imp-bad-line: rgba(220, 38, 38, 0.3);
  --imp-warn: #a16207;
  --imp-warn-bg: rgba(234, 179, 8, 0.1);
  --imp-warn-line: rgba(234, 179, 8, 0.4);

  [data-theme='dark'] & {
    --imp-add: #5ddb96;
    --imp-add-bg: rgba(68, 199, 127, 0.12);
    --imp-add-line: rgba(68, 199, 127, 0.35);
    --imp-upd-bg: rgba(74, 163, 255, 0.13);
    --imp-upd-line: rgba(74, 163, 255, 0.35);
    --imp-bad: #ff8f99;
    --imp-bad-bg: rgba(255, 123, 134, 0.12);
    --imp-bad-line: rgba(255, 123, 134, 0.32);
    --imp-warn: #f5c04e;
    --imp-warn-bg: rgba(245, 192, 78, 0.12);
    --imp-warn-line: rgba(245, 192, 78, 0.35);
  }
`;

const tone = (name) => {
  switch (name) {
    case 'insert': return 'var(--imp-add)';
    case 'update': return 'var(--imp-upd)';
    case 'invalid': return 'var(--imp-bad)';
    case 'warning': return 'var(--imp-warn)';
    default: return 'var(--text)';
  }
};

const toneBg = (name) => {
  switch (name) {
    case 'insert': return 'var(--imp-add-bg)';
    case 'update': return 'var(--imp-upd-bg)';
    case 'invalid': return 'var(--imp-bad-bg)';
    case 'warning': return 'var(--imp-warn-bg)';
    default: return 'var(--input-bg)';
  }
};

const toneLine = (name) => {
  switch (name) {
    case 'insert': return 'var(--imp-add-line)';
    case 'update': return 'var(--imp-upd-line)';
    case 'invalid': return 'var(--imp-bad-line)';
    case 'warning': return 'var(--imp-warn-line)';
    default: return 'var(--input-border)';
  }
};

const EntityTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 12px 5px 10px;
  border-radius: 999px;
  background: var(--imp-upd-bg);
  border: 1px solid var(--imp-upd-line);
  color: var(--primary);
  font-size: 0.85rem;
  font-weight: 600;
`;

/* ── Wybor rodzaju danych ─────────────────────────────────────────────────── */

const EntityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 8px;
`;

const EntityCard = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid var(--input-border);
  background: var(--card-bg);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;

  &:hover {
    border-color: var(--primary);
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover { transform: none; }
  }
`;

const EntityIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  border-radius: 13px;
  background: var(--imp-upd-bg);
  color: var(--primary);
`;

const EntityText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
`;

const EntityName = styled.span`
  font-size: 1.02rem;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const EntityDesc = styled.span`
  font-size: 0.85rem;
  color: var(--text-muted);
  line-height: 1.45;
`;

const EntityArrow = styled.span`
  display: inline-flex;
  color: var(--text-muted);
  flex-shrink: 0;
  transition: transform 0.18s ease, color 0.18s ease;

  ${EntityCard}:hover & {
    color: var(--primary);
    transform: translateX(3px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    ${EntityCard}:hover & { transform: none; }
  }
`;

/* ── Pasek postepu ────────────────────────────────────────────────────────── */

const Stepper = styled.nav`
  display: flex;
  align-items: flex-start;
  margin: 0 0 24px;
`;

/* Kreska laczaca kroki wychodzi ze srodka kolka, wiec siedzi na elemencie, a nie miedzy nimi:
   dzieki temu odcinek „przebyty" barwi sie razem z krokiem, ktory go domknal. */
const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  position: relative;
  min-width: 0;

  &:not(:first-child)::before {
    content: '';
    position: absolute;
    top: 17px;
    right: 50%;
    left: -50%;
    height: 2px;
    background: ${(p) => (p.$done ? 'var(--primary)' : 'var(--input-border)')};
  }
`;

const StepDot = styled.button`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  font-variant-numeric: tabular-nums;
  padding: 0;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  ${(p) => {
    if (p.$active) {
      return css`
        background: var(--primary);
        border: 2px solid var(--primary);
        color: var(--primary-contrast);
        box-shadow: var(--focus-ring);
      `;
    }
    if (p.$done) {
      return css`
        background: var(--card-bg);
        border: 2px solid var(--primary);
        color: var(--primary);
        cursor: pointer;
      `;
    }
    return css`
      background: var(--card-bg);
      border: 2px solid var(--input-border);
      color: var(--text-muted);
    `;
  }}

  &:disabled { cursor: default; }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const StepLabel = styled.span`
  font-size: 0.8rem;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  color: ${(p) => (p.$active ? 'var(--text)' : 'var(--text-muted)')};
  text-align: center;
  padding: 0 6px;

  @media (max-width: 520px) {
    display: ${(p) => (p.$active ? 'block' : 'none')};
  }
`;

/* ── Karty krokow ─────────────────────────────────────────────────────────── */

const StepCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  box-shadow: var(--shadow-sm);
  padding: clamp(18px, 2.4vw, 28px);
  margin-bottom: 24px;
`;

const StepTitle = styled.h2`
  margin: 0 0 6px;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--text);
`;

const StepText = styled.p`
  margin: 0 0 20px;
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--text-muted);
  max-width: 68ch;
`;

const StepActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--input-border);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

/* ── Krok 1: szablon ──────────────────────────────────────────────────────── */

const TemplatePanel = styled.div`
  display: flex;
  gap: 18px;
  padding: 20px;
  border-radius: 14px;
  border: 1px solid var(--input-border);
  background: var(--input-bg);

  @media (max-width: 560px) {
    flex-direction: column;
    gap: 14px;
  }
`;

const TemplateIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: 14px;
  background: var(--imp-add-bg);
  color: var(--imp-add);
`;

const TemplateBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const TemplateList = styled.ul`
  list-style: none;
  margin: 0 0 18px;
  padding: 0;
  display: grid;
  gap: 9px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    font-size: 0.88rem;
    line-height: 1.5;
    color: var(--text);
  }

  svg {
    color: var(--imp-add);
    margin-top: 2px;
  }
`;

const DownloadedNote = styled.p`
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 12px 0 0;
  font-size: 0.85rem;
  color: var(--imp-add);
`;

/* ── Krok 2: pole upuszczania pliku ───────────────────────────────────────── */

const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

const Dropzone = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 200px;
  padding: 28px 20px;
  border-radius: 16px;
  border: 2px dashed ${(p) => (p.$active ? 'var(--primary)' : 'var(--input-border)')};
  background: ${(p) => (p.$active ? 'var(--imp-upd-bg)' : 'var(--input-bg)')};
  cursor: pointer;
  text-align: center;
  transition: border-color 0.18s ease, background 0.18s ease;

  /* Po wybraniu pliku pole kurczy sie do wysokosci karty: pusta plama koloru wokol malego
     kafelka wygladala na niedokonczony uklad, a przeciagac juz nie ma po co. */
  ${(p) => p.$filled && css`
    min-height: 0;
    padding: 20px;
    border-style: solid;
    border-color: var(--imp-add-line);
    background: var(--imp-add-bg);
  `}

  &:hover { border-color: var(--primary); }

  &:focus-visible {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const DropIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--imp-upd-bg);
  color: var(--primary);
`;

const DropTitle = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
`;

const DropHint = styled.span`
  font-size: 0.85rem;
  color: var(--text-muted);
`;

const FileCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  width: min(420px, 100%);
  padding: 14px 16px;
  border-radius: 13px;
  border: 1px solid var(--input-border);
  background: var(--card-bg);
  box-shadow: var(--shadow-sm);
  text-align: left;
`;

const FileIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 11px;
  background: var(--imp-add-bg);
  color: var(--imp-add);
`;

const FileMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileNameText = styled.div`
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.div`
  font-size: 0.8rem;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  margin-top: 2px;
`;

const RemoveFile = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 9px;
  border: 1px solid var(--input-border);
  background: var(--card-bg);
  color: var(--text-muted);
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover { color: var(--imp-bad); border-color: var(--imp-bad-line); }

  &:focus-visible { outline: none; box-shadow: var(--focus-ring); }
`;

const Constraints = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 14px 0 0;
  padding: 0;

  li {
    font-size: 0.78rem;
    color: var(--text-muted);
    background: var(--input-bg);
    border: 1px solid var(--input-border);
    border-radius: 999px;
    padding: 4px 11px;
  }
`;

/* ── Komunikaty ───────────────────────────────────────────────────────────── */

const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 13px 16px;
  border-radius: 12px;
  margin-bottom: 18px;
  font-size: 0.875rem;
  line-height: 1.5;

  svg { flex-shrink: 0; margin-top: 1px; }
`;

const ErrorBanner = styled(Banner)`
  background: var(--imp-bad-bg);
  border: 1px solid var(--imp-bad-line);
  color: var(--imp-bad);
`;

/* E.6: baner kroku 1 dla sprzętu — szablon odbija konfigurację pól magazynu, więc
   kolejność „najpierw skonfiguruj, potem pobierz" musi być widoczna PRZED pobraniem.
   Ton informacyjny (nie ostrzegawczy jak NoteBanner) — to podpowiedź, nie problem. */
const ConfigBanner = styled(Banner)`
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  color: var(--text);
  flex-direction: column;
  gap: 8px;

  a {
    color: var(--primary);
    font-weight: 600;
    text-decoration: underline;
  }
`;

const NoteBanner = styled(Banner)`
  background: var(--imp-warn-bg);
  border: 1px solid var(--imp-warn-line);
  color: var(--imp-warn);
`;

/* ── Podsumowanie ─────────────────────────────────────────────────────────── */

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
`;

const SummaryCard = styled.div`
  padding: 16px 18px;
  border-radius: 13px;
  border: 1px solid ${(p) => (p.$muted ? 'var(--input-border)' : toneLine(p.$tone))};
  background: ${(p) => (p.$muted ? 'var(--input-bg)' : toneBg(p.$tone))};
`;

/* Zero nie dostaje koloru statusu: „0 do aktualizacji" na niebiesko czytalo sie jak wynik,
   ktory sie wydarzyl. Kolor pojawia sie dopiero, gdy jest co liczyc. */
const SummaryValue = styled.div`
  font-size: 1.9rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$muted ? 'var(--text-muted)' : tone(p.$tone))};
`;

const SummaryLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-top: 6px;
`;

/* ── Listy wierszy z problemami ───────────────────────────────────────────── */

const RowsSection = styled.div`
  margin-bottom: 22px;
`;

const RowsSectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 6px;
`;

const RowsSectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0;
  color: ${(p) => tone(p.$tone)};
`;

const Count = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(p) => toneBg(p.$tone)};
  border: 1px solid ${(p) => toneLine(p.$tone)};
  color: ${(p) => tone(p.$tone)};
`;

const RowsHint = styled.p`
  margin: 0 0 10px;
  font-size: 0.83rem;
  color: var(--text-muted);
`;

const RowsList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--input-border);
  border-radius: 12px;
`;

const RowItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--input-border);
  border-left: 3px solid ${(p) => toneLine(p.$tone)};
  background: var(--card-bg);

  &:last-child { border-bottom: none; }

  @media (max-width: 560px) {
    flex-direction: column;
    gap: 8px;
  }
`;

/* Numer wiersza pisany krojem o stalej szerokosci znaku: to wspolrzedna w arkuszu,
   wiec kolumna cyfr ma sie zgadzac w pionie i dac sie porownac z Excelem obok. */
const RowNumber = styled.div`
  flex-shrink: 0;
  min-width: 86px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${(p) => tone(p.$tone)};
  padding-top: 2px;
`;

const RowMessages = styled.div`
  display: grid;
  gap: 6px;
  flex: 1;
  min-width: 0;
  font-size: 0.85rem;
  color: var(--text);
  line-height: 1.45;
`;

const RowMessage = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
`;

const FieldPill = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.75rem;
  padding: 2px 7px;
  border-radius: 6px;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  color: var(--text-muted);
`;

/* ── Wynik ────────────────────────────────────────────────────────────────── */

const ResultHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 22px;
`;

const ResultMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--imp-add-bg);
  border: 1px solid var(--imp-add-line);
  color: var(--imp-add);
`;
